import type { AnchorHTMLAttributes, FC, HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import MdiPlus from '~icons/mdi/plus'
import { openMembershipMoreCreditsModal } from 'src/components/Membership/MembershipMoreCreditsModal'
import { useMyInfo } from 'src/services/apollo/auth'
import type { PriceInfo } from 'src/services/apollo/generated/graphql'
import { useMyQuota } from 'src/services/apollo/quota'
import { cx } from 'src/utils/cx'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import { track } from 'src/utils/tracker'

interface QuotaBalanceProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  moreButtonState?: 'expanded' | 'hidden'
  trackingLocation?: string
  afterOpenMoreCredits?: () => void
}

const QuotaBalance: FC<QuotaBalanceProps> = ({
  className,
  moreButtonState,
  trackingLocation,
  afterOpenMoreCredits,
  ...props
}) => {
  const { data: me } = useMyInfo()
  const { data, loading } = useMyQuota()
  const readonly = !me

  const { t } = useTranslation(['membership'])
  const isSmScreen = useBreakpoint('msm')

  return (
    <Link
      className={cx(
        'flex gap-1 items-center text-lg font-bold font-quicksand text-theme-primary',
        { 'pointer-events-none': readonly },
        className,
      )}
      {...props}
      to={`/@${me?.username}/credits`}
      onClick={() => {
        track('credit_balance_click', {
          location: trackingLocation,
        })
      }}
    >
      <GameIconsCrystalBars className="text-[1em]" />
      <span>{!data && loading ? '…' : data?.toLocaleString()}</span>
      {!readonly && moreButtonState !== 'hidden' && (
        <button
          className={cx(
            'flex-none capitalize hover:bg-theme-primary hover:text-white rounded transition',
            // oxfmt-ignore
            moreButtonState === 'expanded' && 'p-1 pr-2 ml-1 flex items-center text-sm ring-1 ring-theme-primary',
          )}
          onClick={ev => {
            ev.preventDefault()
            openMembershipMoreCreditsModal({
              scene: trackingLocation,
              trackingLocation,
              defaultMode: 'packs',
            })
            afterOpenMoreCredits?.()
          }}
        >
          <MdiPlus className="text-[1em]" />
          {moreButtonState === 'expanded' && (
            <span>
              {t('membership:more-credits.label', {
                context: isSmScreen ? 'short' : null,
              })}
            </span>
          )}
        </button>
      )}
    </Link>
  )
}

export default QuotaBalance

export interface QuotaCostProps extends HTMLAttributes<HTMLDivElement> {
  value?: number | string
  originalValue?: number | string
  loading?: boolean
  iconSize?: number
  iconClassName?: string
  valueClassName?: string
}

export const QuotaCost: FC<QuotaCostProps> = ({
  value,
  className,
  loading,
  iconSize,
  iconClassName,
  valueClassName,
  originalValue: rawOriginalValue,
  ...props
}) => {
  if (value == null) return null

  const originalValue =
    rawOriginalValue && rawOriginalValue === value
      ? undefined
      : rawOriginalValue

  const valueToDisplay =
    typeof value === 'number' ? value.toLocaleString() : value

  return (
    <div
      className={cx(
        'flex gap-2 items-center font-quicksand font-bold text-theme-primary',
        className,
      )}
      {...props}
    >
      <GameIconsCrystalBars
        className={cx('text-2xl', iconClassName)}
        style={{ fontSize: iconSize }}
      />
      <span className={cx(loading && 'opacity-50 transition', valueClassName)}>
        {valueToDisplay}
        {originalValue && (
          <span className="ml-[0.25em] line-through opacity-80">
            {originalValue.toLocaleString()}
          </span>
        )}
      </span>
    </div>
  )
}

export const QuotaCostWithPriceItem: FC<
  Omit<QuotaCostProps, 'value'> & { priceInfo?: PriceInfo; itemKeys: string[] }
> = props => {
  const { priceInfo, itemKeys, ...rest } = props
  if (priceInfo?.actualPrice == null) return null

  const totalPrices = priceInfo.items.filter(
    i => i.key && itemKeys.includes(i.key) && (i.totalPrice ?? 0) !== 0,
  )

  if (totalPrices.length < 1) return null

  const addedValue =
    priceInfo.actualPrice -
    totalPrices.reduce((a, b) => a + (b.totalPrice as number), 0)
  return <QuotaCost value={addedValue} {...rest} />
}
